import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Reportes() {
  const { empresa } = useApp();
  const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0, 7));
  const [periodoAnual, setPeriodoAnual] = useState(String(new Date().getFullYear()));
  const [rv, setRv] = useState(null);
  const [rc, setRc] = useState(null);
  const [er, setEr] = useState(null);

  useEffect(() => {
    if (!empresa) return;
    api.get(`/reportes/registro-ventas?periodo=${periodo}`).then((r) => setRv(r.data));
    api.get(`/reportes/registro-compras?periodo=${periodo}`).then((r) => setRc(r.data));
  }, [empresa, periodo]);

  useEffect(() => {
    if (!empresa) return;
    api.get(`/reportes/estado-resultados?periodo=${periodoAnual}`).then((r) => setEr(r.data));
  }, [empresa, periodoAnual]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Reportes</h1>
        <p className="text-[13px] text-slate-500">Registros de ventas/compras, Libros Electrónicos, Estado de Resultados.</p>
      </div>

      <Tabs defaultValue="ventas">
        <TabsList>
          <TabsTrigger value="ventas" data-testid="tab-r-ventas">Registro Ventas</TabsTrigger>
          <TabsTrigger value="compras" data-testid="tab-r-compras">Registro Compras</TabsTrigger>
          <TabsTrigger value="resultados" data-testid="tab-resultados">Estado de Resultados</TabsTrigger>
        </TabsList>

        <TabsContent value="ventas" className="space-y-3">
          <div className="flex items-center justify-between bg-white border border-slate-200 rounded p-3">
            <div className="flex items-center gap-3">
              <Label className="text-[12px]">Periodo:</Label>
              <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="h-9 w-40" />
            </div>
            <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
          </div>
          {rv && (
            <div className="bg-white border border-slate-200 rounded">
              <div className="p-4 border-b border-slate-200">
                <h2 className="text-base font-bold">REGISTRO DE VENTAS — Periodo {rv.periodo}</h2>
                <p className="text-[11px] text-slate-500">{empresa?.razon_social} • RUC <span className="mono">{empresa?.ruc}</span></p>
              </div>
              <table className="tbl w-full">
                <thead><tr><th>Fecha</th><th>Tipo</th><th>Comprobante</th><th>RUC/DNI Cliente</th><th>Razón Social</th><th className="text-right">Base</th><th className="text-right">IGV</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {rv.ventas.length === 0 ? <tr><td colSpan={8} className="text-center py-6 text-slate-500">Sin movimientos</td></tr>
                    : rv.ventas.map((v) => (
                      <tr key={v.id}>
                        <td className="mono">{v.fecha_emision?.slice(0, 10)}</td>
                        <td>{v.tipo.replace("_", " ")}</td>
                        <td className="mono">{v.serie}-{String(v.numero).padStart(8, "0")}</td>
                        <td className="mono">{v.cliente?.numero_doc}</td>
                        <td>{v.cliente?.razon_social}</td>
                        <td className="tabular text-right">{fmt(v.subtotal_gravado)}</td>
                        <td className="tabular text-right">{fmt(v.igv)}</td>
                        <td className="tabular text-right font-bold">{fmt(v.total)}</td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <td colSpan={5} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-600">TOTALES</td>
                    <td className="tabular text-right px-3 py-2">{fmt(rv.total_base)}</td>
                    <td className="tabular text-right px-3 py-2">{fmt(rv.total_igv)}</td>
                    <td className="tabular text-right px-3 py-2 text-slate-900">{fmt(rv.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="compras" className="space-y-3">
          {rc && (
            <div className="bg-white border border-slate-200 rounded">
              <div className="p-4 border-b border-slate-200">
                <h2 className="text-base font-bold">REGISTRO DE COMPRAS — Periodo {rc.periodo}</h2>
                <p className="text-[11px] text-slate-500">{empresa?.razon_social} • RUC <span className="mono">{empresa?.ruc}</span></p>
              </div>
              <table className="tbl w-full">
                <thead><tr><th>Fecha</th><th>Tipo</th><th>Comprobante</th><th>RUC Proveedor</th><th>Razón Social</th><th className="text-right">Base</th><th className="text-right">IGV</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {rc.compras.length === 0 ? <tr><td colSpan={8} className="text-center py-6 text-slate-500">Sin movimientos</td></tr>
                    : rc.compras.map((c) => (
                      <tr key={c.id}>
                        <td className="mono">{c.fecha_emision?.slice(0, 10)}</td>
                        <td>{c.tipo.replace("_", " ")}</td>
                        <td className="mono">{c.serie}-{c.numero}</td>
                        <td className="mono">{c.proveedor?.numero_doc}</td>
                        <td>{c.proveedor?.razon_social}</td>
                        <td className="tabular text-right">{fmt(c.subtotal_gravado)}</td>
                        <td className="tabular text-right">{fmt(c.igv)}</td>
                        <td className="tabular text-right font-bold">{fmt(c.total)}</td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <td colSpan={5} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-600">TOTALES</td>
                    <td className="tabular text-right px-3 py-2">{fmt(rc.total_base)}</td>
                    <td className="tabular text-right px-3 py-2">{fmt(rc.total_igv)}</td>
                    <td className="tabular text-right px-3 py-2 text-slate-900">{fmt(rc.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="resultados" className="space-y-3">
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded p-3">
            <Label className="text-[12px]">Año:</Label>
            <Input type="number" value={periodoAnual} onChange={(e) => setPeriodoAnual(e.target.value)} className="h-9 w-32 mono" />
          </div>
          {er && (
            <div className="bg-white border border-slate-200 rounded p-6 max-w-2xl">
              <h2 className="text-base font-bold mb-1">ESTADO DE RESULTADOS</h2>
              <p className="text-[11px] text-slate-500 mb-4">Por el ejercicio terminado al 31 de diciembre de {er.periodo} (expresado en Soles)</p>
              <table className="w-full text-[13px]">
                <tbody>
                  <tr className="border-b border-slate-200"><td className="py-2">Ingresos por ventas</td><td className="tabular text-right font-medium">{fmt(er.ingresos)}</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2">(-) Costo de ventas</td><td className="tabular text-right text-rose-600">({fmt(er.costo_ventas)})</td></tr>
                  <tr className="border-b border-slate-300 font-bold"><td className="py-2">Utilidad Bruta</td><td className="tabular text-right">{fmt(er.utilidad_bruta)}</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2">(-) Gastos de personal</td><td className="tabular text-right text-rose-600">({fmt(er.gastos_personal)})</td></tr>
                  <tr className="border-b border-slate-300 font-bold"><td className="py-2">Utilidad Operativa</td><td className="tabular text-right">{fmt(er.utilidad_operativa)}</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2">(-) Impuesto a la Renta (29.5%)</td><td className="tabular text-right text-rose-600">({fmt(er.impuesto_renta)})</td></tr>
                  <tr className="border-y-2 border-slate-900 font-bold text-base"><td className="py-3">UTILIDAD NETA</td><td className="tabular text-right">{fmt(er.utilidad_neta)}</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
